<script setup lang="ts">
defineOptions({ name: "SiteTemplateEditor" });

const config = useRuntimeConfig();
const route = useRoute();
const toast = useToast();
const { t } = useI18n();
const { $api } = useNuxtApp();

const iframeEl = ref<HTMLIFrameElement | null>(null);

const iframeSrc = computed(() => {
  const clientAppUrl = String(config.public.clientAppUrl || "").replace(/\/$/, "");
  return `${clientAppUrl}/site-templates/builder/${route.params.id}`;
});

function getAssetType(mimeType: string) {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "file";
}

async function uploadAsset(file: File) {
  const asset = await $api<any>("/assets", {
    method: "POST",
    body: {
      name: file.name,
      originalName: file.name,
      type: getAssetType(file.type),
      mimeType: file.type,
      size: file.size,
    },
  });

  if (asset.status === "ready") return asset;

  const presignedResponse = await $api<{
    uploadURL: string;
    uploadHeaders: Record<string, string>;
  }>(`/assets/${asset.id}/presigned-url`);

  await fetch(presignedResponse.uploadURL, {
    method: "PUT",
    body: file,
    headers: {
      ...presignedResponse.uploadHeaders,
      "Content-Type": file.type,
    },
  });

  return await $api<any>(`/assets/${asset.id}/confirm`, { method: "PUT" });
}

async function loadAssets(ids: string[]) {
  const resp = await $api<any>("/assets", {
    params: { ids: ids.join(","), pageSize: ids.length },
  });
  return resp.items || [];
}

const { formModel, load, save, errorMsg } = useCrud<any>({
  endpoint: "/site-templates",
  defaultFormModel: () => ({
    id: "",
    site: {},
    pages: [],
    widgets: [],
    forms: [],
  }),
  serialize: (model) => ({
    id: model.id,
    site: model.site,
    pages: model.pages,
    widgets: model.widgets,
    forms: model.forms ?? [],
  }),
});

async function init() {
  try {
    await load(route.params.id as string);

    iframeEl.value?.contentWindow?.postMessage(
      {
        type: "template:init",
        data: JSON.parse(
          JSON.stringify({
            site: formModel.site,
            pages: formModel.pages ?? [],
            widgets: formModel.widgets ?? [],
            forms: formModel.forms ?? [],
          }),
        ),
      },
      config.public.clientAppUrl,
    );
  } catch {
    toast.error({ title: t("message.loadFailed") });
  }
}

async function onSave(data: {
  site: any;
  pages: any[];
  widgets: any[];
  forms?: any[];
}) {
  Object.assign(formModel, data);
  try {
    await save();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

async function onMessage(event: MessageEvent) {
  if (!event.data?.type?.startsWith("builder:")) return;

  if (event.data.type === "builder:ready") {
    init();
  }
  if (event.data.type === "builder:save") {
    onSave(event.data.data);
  }
  if (event.data.type === "builder:asset-upload") {
    try {
      const asset = await uploadAsset(event.data.file);
      iframeEl.value?.contentWindow?.postMessage(
        {
          type: "template:asset-uploaded",
          requestID: event.data.requestID,
          data: asset,
        },
        config.public.clientAppUrl,
      );
    } catch (error) {
      iframeEl.value?.contentWindow?.postMessage(
        {
          type: "template:asset-error",
          requestID: event.data.requestID,
          error: String(error),
        },
        config.public.clientAppUrl,
      );
    }
  }
  if (event.data.type === "builder:asset-load") {
    try {
      const assets = await loadAssets(event.data.ids ?? []);
      iframeEl.value?.contentWindow?.postMessage(
        {
          type: "template:asset-loaded",
          requestID: event.data.requestID,
          data: assets,
        },
        config.public.clientAppUrl,
      );
    } catch (error) {
      iframeEl.value?.contentWindow?.postMessage(
        {
          type: "template:asset-error",
          requestID: event.data.requestID,
          error: String(error),
        },
        config.public.clientAppUrl,
      );
    }
  }
  if (event.data.type === "builder:exit") {
    navigateTo("/site-template");
  }
}

onMounted(() => window.addEventListener("message", onMessage));
onUnmounted(() => window.removeEventListener("message", onMessage));
</script>

<template>
  <div class="builder-page">
    <iframe
      ref="iframeEl"
      :src="iframeSrc"
      class="builder-page__iframe"
      allow="clipboard-write"
    />
  </div>
</template>

<style scoped lang="scss">
.builder-page {
  position: fixed;
  inset: 0;
  z-index: 9999;

  &__iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
}
</style>
